import React, { useState, useEffect } from 'react';
import {
  X,
  Church,
  MapPin,
  Calendar,
  Trophy,
  Award,
  Loader2,
  Sparkles,
} from 'lucide-react';
import { getCountryInfo } from '../utils/countryHelper.ts';
import { FriendButton, FriendshipStatus } from './FriendButton.tsx';
import { soundEngine } from '../utils/soundAndSpeech.ts';
import {
  calculateLevelFromXp,
  formatLifetimeAccuracy,
  formatDisplayNumber,
  getLevelProgressTooltipText,
  getLifetimeAccuracyTooltipText,
} from '../config/scoringConfig.ts';
import { InfoTooltip } from './ui/InfoTooltip.tsx';


interface UserPublicProfileData {
  uid: string;
  username: string;
  displayName: string;
  avatarUrl: string;
  titleBadge: string;
  xp?: number;
  level?: number;
  levelInfo?: {
    level: number;
    currentLevelXp: number;
    nextLevelXp: number;
    progressPercent: number;
    rankTitle: string;
    summaryText: string;
  };
  lifetimeAccuracy?: {
    unlocked: boolean;
    percent: number | null;
    label: string;
    questionsSeen: number;
    correctCount: number;
  };
  church: string | null;
  location: { city: string; country: string } | null;
  country: string;
  dateJoined: string;
  ranks: {
    weekly: number;
    monthly: number;
    allTime: number;
    country: number;
  };
  completions: {
    total: number;
    solo: number;
    multiplayer: number;
    live: number;
  };
  friendshipStatus: FriendshipStatus;
}

interface UserProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  targetUserUidOrUsername: string | null;
  currentUserUid: string;
  onFriendshipStatusChanged?: (targetUid: string, newStatus: FriendshipStatus) => void;
}

export const UserProfileModal: React.FC<UserProfileModalProps> = ({
  isOpen,
  onClose,
  targetUserUidOrUsername,
  currentUserUid,
  onFriendshipStatusChanged,
}) => {
  const [profile, setProfile] = useState<UserPublicProfileData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !targetUserUidOrUsername) {
      setProfile(null);
      setError(null);
      return;
    }

    let isMounted = true;
    const fetchProfile = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const url = `/api/users/${encodeURIComponent(targetUserUidOrUsername)}/profile${
          currentUserUid ? `?viewerUid=${encodeURIComponent(currentUserUid)}` : ''
        }`;
        const res = await fetch(url);
        if (!res.ok) {
          throw new Error('Could not load user profile');
        }
        const data = await res.json();
        if (isMounted) {
          setProfile(data);
        }
      } catch (err: any) {
        if (isMounted) {
          setError(err.message || 'Failed to load profile');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchProfile();

    return () => {
      isMounted = false;
    };
  }, [isOpen, targetUserUidOrUsername, currentUserUid]);

  if (!isOpen) return null;

  const isSelf = currentUserUid && profile?.uid === currentUserUid;
  const countryInfo = getCountryInfo(profile?.location?.country || profile?.country);
  const resolvedLevelInfo =
    profile?.levelInfo || calculateLevelFromXp(Number(profile?.xp || 0));
  const resolvedLifetimeAcc =
    profile?.lifetimeAccuracy || formatLifetimeAccuracy(0, 0);
  const xpFormatted = formatDisplayNumber(profile?.xp || 0);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 backdrop-blur-xs p-4 sm:p-6 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-3xl bg-[#141414] border border-[#262626] p-5 sm:p-7 shadow-2xl text-white relative space-y-6 my-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top Header / Close Button */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img
              src="/quickibible-icon.png"
              alt="QuickiBible"
              className="w-5 h-5 rounded-md object-contain shadow-xs"
            />
            <span className="text-xs font-bold uppercase tracking-wider text-[#A3A3A3]">
              Scholar Profile
            </span>
          </div>
          <button
            type="button"
            onClick={() => {
              soundEngine.playClick();
              onClose();
            }}
            title="Close profile"
            className="p-1.5 rounded-xl border border-[#404040] bg-[#1C1C1C] hover:bg-[#262626] text-[#A3A3A3] hover:text-white transition-colors cursor-pointer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {isLoading ? (
          <div className="py-16 text-center space-y-3">
            <Loader2 className="w-8 h-8 animate-spin text-[#F97316] mx-auto" />
            <p className="text-xs text-[#A3A3A3]">Loading scholar profile...</p>
          </div>
        ) : error || !profile ? (
          <div className="py-12 text-center space-y-3">
            <p className="text-sm font-semibold text-red-400">
              {error || 'Scholar not found'}
            </p>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl bg-[#262626] text-xs font-semibold hover:bg-[#333333]"
            >
              Back
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Identity Header */}
            <div className="flex items-start justify-between gap-4 flex-wrap sm:flex-nowrap">
              <div className="flex items-start gap-4">
                {profile.avatarUrl ? (
                  <img
                    src={profile.avatarUrl}
                    alt={profile.displayName}
                    className="w-16 h-16 sm:w-18 sm:h-18 rounded-2xl object-cover border-2 border-[#262626] shadow-sm shrink-0"
                  />
                ) : (
                  <div className="w-16 h-16 sm:w-18 sm:h-18 rounded-2xl bg-[#1C1C1C] border-2 border-[#262626] text-[#A3A3A3] font-extrabold text-xl flex items-center justify-center shadow-sm shrink-0">
                    {profile.username.slice(0, 2).toUpperCase()}
                  </div>
                )}

                <div className="space-y-1 min-w-0">
                  <h2 className="text-lg sm:text-xl font-extrabold text-[#FAFAFA] leading-tight truncate">
                    {profile.displayName}
                  </h2>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-mono text-[#A3A3A3]">
                      @{profile.username}
                    </span>
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-[#F97316]/15 border border-[#F97316]/30 text-[#F97316] text-[11px] font-bold">
                      <Sparkles className="w-3 h-3" />
                      {profile.titleBadge}
                    </span>
                  </div>
                </div>
              </div>

              {/* Add Friend Button or Self Tag */}
              <div className="shrink-0 w-full sm:w-auto flex sm:justify-end">
                {isSelf ? (
                  <span className="px-3 py-1.5 rounded-xl border border-[#404040] bg-[#1C1C1C] text-xs text-[#A3A3A3] font-medium">
                    This is you
                  </span>
                ) : (
                  <FriendButton
                    currentUserUid={currentUserUid}
                    targetUserUid={profile.uid}
                    initialStatus={profile.friendshipStatus}
                    size="md"
                    onStatusChange={(newStatus) => {
                      setProfile({ ...profile, friendshipStatus: newStatus });
                      onFriendshipStatusChanged?.(profile.uid, newStatus);
                    }}
                  />
                )}
              </div>
            </div>

            {/* Level Progress & Lifetime Accuracy Bar */}
            <div className="p-3.5 rounded-2xl bg-[#1C1C1C] border border-[#262626] space-y-2">
              <div className="flex items-center justify-between text-xs font-mono">
                <div className="flex items-center gap-1.5">
                  <span className="font-bold text-[#FAFAFA]">
                    {resolvedLevelInfo.summaryText}
                  </span>
                  <InfoTooltip
                    ariaLabel="How Level is calculated"
                    content={getLevelProgressTooltipText()}
                  />
                </div>
                <span
                  className={`font-bold tabular-nums ${
                    xpFormatted.isZero ? 'text-[#737373]' : 'text-amber-400'
                  }`}
                >
                  {xpFormatted.isZero ? '–' : `${xpFormatted.text} XP`}
                </span>
              </div>
              <div className="w-full h-2 rounded-full bg-[#141414] overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-[#FF8A00] to-[#EA580C] rounded-full transition-all duration-500"
                  style={{
                    width: `${Math.min(100, Math.max(4, resolvedLevelInfo.progressPercent))}%`,
                  }}
                />
              </div>
              <div className="flex items-center justify-between text-[11px] text-[#A3A3A3] pt-0.5">
                <div className="flex items-center gap-1.5">
                  <span>Lifetime Accuracy</span>
                  <InfoTooltip
                    ariaLabel="How Lifetime Accuracy is calculated"
                    content={getLifetimeAccuracyTooltipText()}
                  />
                </div>
                <span
                  className={`font-mono ${
                    resolvedLifetimeAcc.unlocked
                      ? 'text-emerald-400 font-bold'
                      : 'text-[#737373]'
                  }`}
                >
                  {resolvedLifetimeAcc.label}
                </span>
              </div>
            </div>


            {/* Meta Information Cards (Church, Location, Joined) */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {/* Church */}
              <div className="p-3.5 rounded-2xl bg-[#1C1C1C] border border-[#262626] flex items-center gap-3">
                <div className="w-8 h-8 rounded-xl bg-[#262626] text-[#F97316] flex items-center justify-center shrink-0">
                  <Church className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] uppercase font-bold tracking-wider text-[#737373]">
                    Church / Fellowship
                  </div>
                  <div className="text-xs font-semibold text-[#FAFAFA] truncate">
                    {profile.church ? (
                      profile.church
                    ) : (
                      <span className="text-[#737373] italic">Private (Hidden)</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Location */}
              <div className="p-3.5 rounded-2xl bg-[#1C1C1C] border border-[#262626] flex items-center gap-3">
                <div className="w-8 h-8 rounded-xl bg-[#262626] text-emerald-400 flex items-center justify-center shrink-0">
                  <MapPin className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] uppercase font-bold tracking-wider text-[#737373]">
                    Location
                  </div>
                  <div className="text-xs font-semibold text-[#FAFAFA] truncate">
                    {profile.location ? (
                      `${profile.location.city ? `${profile.location.city}, ` : ''}${countryInfo.name} ${countryInfo.flag}`
                    ) : (
                      <span className="text-[#737373] italic">Private (Hidden)</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Date Joined */}
              <div className="p-3.5 rounded-2xl bg-[#1C1C1C] border border-[#262626] flex items-center gap-3 sm:col-span-2">
                <div className="w-8 h-8 rounded-xl bg-[#262626] text-amber-400 flex items-center justify-center shrink-0">
                  <Calendar className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] uppercase font-bold tracking-wider text-[#737373]">
                    Scripture Journey Began
                  </div>
                  <div className="text-xs font-semibold text-[#FAFAFA]">
                    Member since {profile.dateJoined}
                  </div>
                </div>
              </div>
            </div>

            {/* Current Ranks Section */}
            <div className="space-y-2.5">
              <div className="flex items-center gap-1.5 text-xs font-bold text-[#D4D4D4] uppercase tracking-wider">
                <Trophy className="w-3.5 h-3.5 text-amber-500" />
                <span>Current Leaderboard Ranks</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                <div className="p-3 rounded-2xl bg-[#1C1C1C] border border-[#262626] text-center">
                  <div className="text-[10px] uppercase font-bold text-[#737373]">
                    Weekly
                  </div>
                  <div className="text-lg font-mono font-extrabold text-[#FAFAFA] mt-0.5">
                    #{profile.ranks.weekly}
                  </div>
                </div>
                <div className="p-3 rounded-2xl bg-[#1C1C1C] border border-[#262626] text-center">
                  <div className="text-[10px] uppercase font-bold text-[#737373]">
                    Monthly
                  </div>
                  <div className="text-lg font-mono font-extrabold text-[#FAFAFA] mt-0.5">
                    #{profile.ranks.monthly}
                  </div>
                </div>
                <div className="p-3 rounded-2xl bg-[#1C1C1C] border border-[#262626] text-center">
                  <div className="text-[10px] uppercase font-bold text-[#737373]">
                    All-Time
                  </div>
                  <div className="text-lg font-mono font-extrabold text-amber-400 mt-0.5">
                    #{profile.ranks.allTime}
                  </div>
                </div>
                <div className="p-3 rounded-2xl bg-[#1C1C1C] border border-[#262626] text-center">
                  <div className="text-[10px] uppercase font-bold text-[#737373]">
                    {countryInfo.flag} Country
                  </div>
                  <div className="text-lg font-mono font-extrabold text-emerald-400 mt-0.5">
                    #{profile.ranks.country}
                  </div>
                </div>
              </div>
            </div>

            {/* Quiz Completions Breakdown */}
            <div className="space-y-2.5">
              <div className="flex items-center gap-1.5 text-xs font-bold text-[#D4D4D4] uppercase tracking-wider">
                <Award className="w-3.5 h-3.5 text-[#F97316]" />
                <span>Quiz Completions & Mastery</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                {[
                  { label: 'Total Quizzes', val: profile.completions.total },
                  { label: 'Solo Mode', val: profile.completions.solo },
                  { label: 'Multiplayer', val: profile.completions.multiplayer },
                  { label: 'Live Events', val: profile.completions.live },
                ].map((stat) => {
                  const fmt = formatDisplayNumber(stat.val);
                  return (
                    <div
                      key={stat.label}
                      className="p-3 rounded-2xl bg-[#1C1C1C] border border-[#262626] text-center"
                    >
                      <div className="text-[10px] uppercase font-bold text-[#737373]">
                        {stat.label}
                      </div>
                      <div
                        className={`text-base font-mono font-bold mt-0.5 ${
                          fmt.isZero ? 'text-[#737373]' : 'text-[#FAFAFA]'
                        }`}
                      >
                        {fmt.text}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
